import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader, Section } from "@/components/Common";
import { Calendar, User, FileText, Download, ExternalLink, ArrowRight, LifeBuoy, BookOpen, Users } from "lucide-react";
import api from "@/lib/api";

export function Gallery() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/gallery").then(r => setItems(r.data || [])); }, []);
  return (
    <div data-testid="gallery-page">
      <PageHeader eyebrow="Photo Gallery" title="Moments that made us who we are." subtitle="A visual journey through community gatherings, ceremonies, and service days." />
      <Section>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <div key={it.id} data-testid={`gallery-${it.id}`}
              className={`rounded-2xl overflow-hidden card-soft group ${i % 5 === 0 ? "md:col-span-2 md:row-span-2" : ""}`}>
              <div className="relative h-full w-full aspect-square">
                <img src={it.image_url} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bcol-navy)]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
                <div className="absolute bottom-3 left-3 right-3 text-white opacity-0 group-hover:opacity-100 transition">
                  <div className="font-display text-lg">{it.title}</div>
                  {it.caption && <div className="text-xs text-white/80">{it.caption}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

export function Blog() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then(r => setPosts(r.data || [])); }, []);
  return (
    <div data-testid="blog-page">
      <PageHeader eyebrow="News & Stories" title="From the community, for the community." subtitle="Reports, announcements, member spotlights, and stories from the diaspora." />
      <Section>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
          {posts.map((p) => (
            <Link to={`/blog/${p.id}`} key={p.id} className="card-soft overflow-hidden block" data-testid={`blog-${p.id}`}>
              <div className="h-52 overflow-hidden"><img src={p.image_url} className="w-full h-full object-cover hover:scale-105 transition duration-500" alt={p.title} /></div>
              <div className="p-6">
                <span className="inline-block text-[10px] font-semibold tracking-widest uppercase bg-[var(--bcol-gold)]/10 text-[var(--bcol-navy)] px-2 py-1 rounded mb-3">{p.tag}</span>
                <h3 className="font-display text-xl text-[var(--bcol-navy)] mb-2">{p.title}</h3>
                <p className="text-sm text-[var(--bcol-muted)] line-clamp-3 mb-4">{p.excerpt}</p>
                <div className="flex items-center gap-3 text-xs text-[var(--bcol-muted)]">
                  <span className="flex items-center gap-1"><User className="w-3 h-3" /> {p.author}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}

export function BlogPost() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/blog/${id}`).then(r => setP(r.data)).catch(() => setErr(true)); }, [id]);

  if (err) return (
    <Section>
      <p className="text-center text-[var(--bcol-muted)]">Story not found. <Link to="/blog" className="text-[var(--bcol-navy)] underline">Back to news</Link></p>
    </Section>
  );
  if (!p) return <Section><div className="h-64 animate-pulse bg-white card-soft" /></Section>;

  return (
    <div data-testid="blog-post-page">
      <PageHeader eyebrow={p.tag} title={p.title} subtitle={`By ${p.author} · ${new Date(p.created_at).toLocaleDateString()}`} image={p.image_url} />
      <Section>
        <article className="max-w-3xl mx-auto prose prose-lg">
          <img src={p.image_url} alt="" className="rounded-2xl w-full mb-10" />
          <p className="text-[var(--bcol-muted)] leading-relaxed text-lg whitespace-pre-line">{p.content}</p>
          <Link to="/blog" className="btn-outline mt-10" data-testid="blog-back">← Back to all stories</Link>
        </article>
      </Section>
    </div>
  );
}

export function Reports() {
  const reports = [
    {
      year: "2025",
      title: "Professional Annual Report",
      url: "https://customer-assets.emergentagent.com/job_community-voice-71/artifacts/6ied6q71_BCOL_2025_Professional_Annual_Report.pdf",
      size: "PDF",
    },
  ];
  return (
    <div data-testid="reports-page">
      <PageHeader eyebrow="Transparency" title="Annual Reports & Financials" subtitle="As a 501(c)(3) nonprofit, we believe in radical transparency. Read our annual reports below." />
      <Section>
        <h3 className="font-display text-2xl text-[var(--bcol-navy)] mb-6">Download Reports</h3>
        <div className="space-y-4 max-w-3xl">
          {reports.map((r) => (
            <a key={r.year} href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`report-${r.year}`}
              className="flex items-center justify-between p-6 card-soft hover:border-[var(--bcol-navy)]">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[var(--bcol-navy)] text-[var(--bcol-gold)] flex items-center justify-center"><FileText className="w-6 h-6" /></div>
                <div>
                  <div className="font-display text-lg text-[var(--bcol-navy)]">{r.year} — {r.title}</div>
                  <div className="text-xs text-[var(--bcol-muted)]">{r.size}</div>
                </div>
              </div>
              <Download className="w-5 h-5 text-[var(--bcol-muted)]" />
            </a>
          ))}
        </div>
      </Section>
    </div>
  );
}

export function Resources() {
  const groups = [
    { i: LifeBuoy, t: "Family & Newcomer Support", items: ["Housing navigation", "Health & wellness clinics", "Legal aid referrals", "School enrollment assistance"] },
    { i: BookOpen, t: "Education & Scholarships", items: ["Scholarship application info", "Tutoring partners", "College access workshops", "Mentorship circles"] },
    { i: Users, t: "Community Programs", items: ["Cultural language classes", "Youth leadership academy", "Senior gatherings", "Small business workshops"] },
  ];
  return (
    <div data-testid="resources-page">
      <PageHeader eyebrow="Community Resources" title="A starting point for support." subtitle="Curated resources and partner referrals for families across the African diaspora in Ohio." />
      <Section>
        <div className="grid lg:grid-cols-3 gap-7">
          {groups.map(({ i: Icon, t, items }) => (
            <div key={t} className="card-soft p-7" data-testid={`resource-${t.toLowerCase().split(" ")[0]}`}>
              <div className="w-12 h-12 rounded-xl bg-[var(--bcol-navy)] text-[var(--bcol-gold)] flex items-center justify-center mb-5"><Icon className="w-6 h-6" /></div>
              <h3 className="font-display text-xl text-[var(--bcol-navy)] mb-3">{t}</h3>
              <ul className="space-y-2 text-sm text-[var(--bcol-muted)]">
                {items.map((it) => <li key={it} className="flex items-start gap-2"><ArrowRight className="w-3 h-3 mt-1 text-[var(--bcol-gold)]" /> {it}</li>)}
              </ul>
              <Link to="/contact" className="text-sm text-[var(--bcol-navy)] underline mt-5 inline-flex items-center gap-1">Request a referral <ExternalLink className="w-3 h-3" /></Link>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
